import { Spacing } from '@persona-id/design-tokens';
import { ReactElement } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { Period } from 'dashboard/lib/unixTime';
import { fillChartData, generateDomain, getEffectiveInterval } from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHourChart,
  formatChartMonthName,
  formatNumber,
} from 'dashboard/lib/utils/formatters';
import {
  ReportRunTypes,
  ReportsChartDatum,
  ReportsMetricsSummary,
  ReportsOverTimeDatum,
  ReportsSummaryDatum,
  ReportsSummaryOverTime,
} from 'dashboard/models/metrics-reports';
import { ReportsMetricsState, ReportTemplateToken } from 'dashboard/redux/reducers/metrics-reports';
import ReportsComposedChart from 'dashboard/views/DashboardReportsAnalyticsView/ReportsComposedChart';
import ReportsDateGranularitySelector from 'dashboard/views/DashboardReportsAnalyticsView/ReportsDateGranularitySelector';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

const ReportsMetricsContainer = styled.section`
  width: 100%;
  padding: 0px ${Spacing[6]} ${Spacing[6]} ${Spacing[6]};
  font-family: ${({ theme }) => theme.text.fontFamily.base};
`;

const StyledCardTitle = styled(Card.Title)`
  font-weight: ${FontWeight.Medium};
  padding-bottom: ${Spacing[2]};
`;

// Height is chart height + card padding
const ReportsComposedCardBody = styled(Card.Body)`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-weight: ${FontWeight.Regular};
  font-size: 8px;
  line-height: 125%;
  padding-top: 0px;
`;

const ReportsChartTitle = styled(Card.Title)`
  font-weight: ${FontWeight.Medium};
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  color: ${({ theme }) => theme.color.text.secondary};
`;

const MetricsCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const ReportsMetricsSummaryContainer = styled.div`
  width: 100%;
  margin-left: ${Spacing[8]};
  margin-top: ${Spacing[4]};
`;

const SummaryMetric = styled.div`
  margin-top: ${Spacing[2]};
  align-items: center;
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.color.text.secondary};
`;

const SummaryMetricValue = styled.div`
  flex-direction: column;
  display: flex;
  display: flex;
  color: ${({ theme }) => theme.color.text.primary};
`;

const SummaryContainer = styled.div`
  position: relative;
  margin-right: 300px;
  grid-gap: ${Spacing[4]};
  flex-direction: row;
  grid-template-columns: 1fr 1fr 1fr;
  display: grid;
`;

const SummaryTitle = styled.div`
  font-weight: ${FontWeight.Regular};
  font-size: 16px;
  line-height: 24px;
`;

export interface ReportMetricsProps
  extends Pick<
    ReportsMetricsState,
    'volumeSummary' | 'reportsOverTime' | 'lastQueryFilters' | 'granularities'
  > {
  loading: boolean;
  queriedAt: Iso8601DateString;
  handleUpdateGranularity: (
    templateIds: ReportTemplateToken[],
    templateId: ReportTemplateToken,
    period: Period,
  ) => void;
  templateIds: ReportTemplateToken[];
}

function titleize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).split('_').join(' ');
}

function calculatePercentage(numerator: number, denominator: number, decimals = 2) {
  return denominator === 0
    ? 'N/A'
    : // In the event the number is greater than 1 %, we'll default to 2 decimals, even if they provide 4
      `${formatNumber((numerator * 100) / denominator, {
        maximumFractionDigits: (numerator * 100) / denominator > 1 ? 2 : decimals,
      })}%`;
}

const MATCH_RATE_SUFFIX = '_match_rate';
const READY_SUFFIX = '_ready_count';
const RUN_SUFFIX = '_run_count';

type BaseValue = Omit<ReportsChartDatum, 'created_timestamp'>;

const BASE_VALUE: BaseValue = {
  first_ready_count: 0,
  first_match_rate: 0,
  subsequent_ready_count: 0,
  subsequent_match_rate: 0,
};

const LEGEND_ICONS: Record<keyof BaseValue, JSX.Element> = {
  first_ready_count: <HelpIcon> Number of reports ready to be viewed on first run. </HelpIcon>,
  subsequent_ready_count: (
    <HelpIcon> Number of reports ready to be viewed on subsequent runs. </HelpIcon>
  ),
  first_match_rate: (
    <HelpIcon> Percentage of ready reports with at least 1 matched result on first runs. </HelpIcon>
  ),
  subsequent_match_rate: (
    <HelpIcon>
      {' '}
      Percentage of ready reports with at least 1 matched result on subsequent runs.{' '}
    </HelpIcon>
  ),
};

const NON_RECURRING_ICONS: Partial<Record<keyof ReportsChartDatum, ReactElement>> = {
  first_ready_count: <HelpIcon> Number of reports ready to be viewed. </HelpIcon>,
  first_match_rate: <HelpIcon> Percentage of reports with at least 1 matched result. </HelpIcon>,
};

export const MetricsDivider = styled.div`
  margin-top: ${BASE / 4}px;
  padding-top: ${BASE / 4}px;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  margin-right: 300px;
`;

const EMPTY_FIRST_DATUM: ReportsSummaryDatum = {
  created_count: 0,
  ready_count: 0,
  matched_count: 0,
  run_type: ReportRunTypes.First,
};

const EMPTY_SUBSEQUENT_DATUM: ReportsSummaryDatum = {
  created_count: 0,
  ready_count: 0,
  matched_count: 0,
  run_type: ReportRunTypes.Subsequent,
};

const RECURRABLE_REPORT_TYPES = [
  'Report::AdverseMedia',
  'Report::CryptoAddressWatchlist',
  'Report::BusinessAdverseMedia',
  'Report::BusinessWatchlist',
  'Report::PoliticallyExposedPerson',
  'Report::Watchlist',
];

const ORDERED_REPORT_TYPES = [
  'Report::Watchlist',
  'Report::AdverseMedia',
  'Report::PoliticallyExposedPerson',
  'Report::BusinessWatchlist',
  'Report::BusinessAdverseMedia',
  'Report::EmailAddress',
  'Report::PhoneNumber',
];

const BASIC_SUMMARY_ICONS: Record<string, ReactElement> = {
  [READY_SUFFIX]: (
    <HelpIcon>
      {' '}
      Percentage of ran reports that are ready to be viewed. Remaining reports have errored.
    </HelpIcon>
  ),
  [MATCH_RATE_SUFFIX]: (
    <HelpIcon> Percentage of ready reports with at least 1 matched result. </HelpIcon>
  ),
  [RUN_SUFFIX]: <HelpIcon> Number of reports that were ran. </HelpIcon>,
};

const RUN_TYPE_SUMMARY_ICONS: Record<
  keyof BaseValue | 'first_run_count' | 'subsequent_run_count',
  JSX.Element
> = {
  first_ready_count: (
    <HelpIcon>
      {' '}
      Percentage of first runs that are ready to be viewed. Remaining reports have errored.{' '}
    </HelpIcon>
  ),
  subsequent_ready_count: (
    <HelpIcon>
      {' '}
      Percentage of subsequent runs that are ready to be viewed. Remaining reports have errored.{' '}
    </HelpIcon>
  ),
  first_match_rate: (
    <HelpIcon> Percentage of ready reports with at least 1 matched result on first runs. </HelpIcon>
  ),
  subsequent_match_rate: (
    <HelpIcon>
      {' '}
      Percentage of ready reports with at least 1 matched result on subsequent runs.{' '}
    </HelpIcon>
  ),
  first_run_count: <HelpIcon> Number of first runs. </HelpIcon>,
  subsequent_run_count: (
    <HelpIcon>
      {' '}
      Number of subsequent runs through continuous monitoring or manual re-runs.{' '}
    </HelpIcon>
  ),
};

const formatSummaryDatum = (summaryDatum: ReportsSummaryDatum, isRecurrable: boolean) => {
  // Populate the summary portions
  return (
    <>
      {isRecurrable && (
        <>
          <SummaryTitle>{`${titleize(summaryDatum.run_type)} runs`}</SummaryTitle>
          <MetricsDivider></MetricsDivider>
        </>
      )}
      <SummaryContainer>
        <SummaryMetric>
          Runs{' '}
          {isRecurrable
            ? RUN_TYPE_SUMMARY_ICONS[`${summaryDatum.run_type}${RUN_SUFFIX}`]
            : BASIC_SUMMARY_ICONS[RUN_SUFFIX]}
          <SummaryMetricValue>{formatNumber(summaryDatum.created_count)}</SummaryMetricValue>
        </SummaryMetric>
        <SummaryMetric>
          Ready • Rate{' '}
          {isRecurrable
            ? RUN_TYPE_SUMMARY_ICONS[`${summaryDatum.run_type}${READY_SUFFIX}`]
            : BASIC_SUMMARY_ICONS[READY_SUFFIX]}
          <SummaryMetricValue>
            {formatNumber(summaryDatum.ready_count)} •{` `}
            {calculatePercentage(summaryDatum.ready_count, summaryDatum.created_count)}
          </SummaryMetricValue>
        </SummaryMetric>
        <SummaryMetric>
          Matched •{` `}Rate{' '}
          {isRecurrable
            ? RUN_TYPE_SUMMARY_ICONS[`${summaryDatum.run_type}${MATCH_RATE_SUFFIX}`]
            : BASIC_SUMMARY_ICONS[MATCH_RATE_SUFFIX]}
          <SummaryMetricValue>
            {formatNumber(summaryDatum.matched_count)} •{` `}
            {calculatePercentage(summaryDatum.matched_count, summaryDatum.ready_count, 4)}
          </SummaryMetricValue>
        </SummaryMetric>
      </SummaryContainer>
      <Spacer height={2} />
    </>
  );
};

const ReportMetrics = ({
  loading,
  volumeSummary,
  reportsOverTime,
  granularities,
  templateIds,
  handleUpdateGranularity,
  lastQueryFilters,
}: ReportMetricsProps) => {
  const nullState = (
    <TableNullState dataTestBlock='report-metrics'>
      <Heading size='sm'>No matched results. Try adjusting your filters to view data.</Heading>
    </TableNullState>
  );

  // Based off of the index of the important report types, reorder the volume summary so we always show these report types first
  // 1. Watchlist
  // 2. PEP
  // 3. Adverse Media
  // 4. Everything else
  const volumeSummaryOrder: Record<string, number> = {};
  volumeSummary.forEach((summary) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    volumeSummaryOrder[summary.id] = ORDERED_REPORT_TYPES.includes(summary.type)
      ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ORDERED_REPORT_TYPES.indexOf(summary.type)
      : ORDERED_REPORT_TYPES.length;
  });
  const orderedVolumeSummary: ReportsMetricsSummary[] = volumeSummary.sort(
    (a, b) => volumeSummaryOrder[a.id] - volumeSummaryOrder[b.id],
  );

  return (
    <ReportsMetricsContainer data-test='reports-metrics__data-section'>
      {!loading && orderedVolumeSummary.length === 0
        ? nullState
        : orderedVolumeSummary.map((summaryData: ReportsMetricsSummary) => {
            // Find the overTime information that correlates to the summary. There will never be a case where we have
            // summary and not over time info, so this is a safe procedure
            const overTimeData = reportsOverTime.data.find((data) => {
              return data.id === summaryData.id;
            });

            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const isRecurrable = RECURRABLE_REPORT_TYPES.includes(summaryData.type);

            // Now lets bring out the data from both
            const summaryObservations = summaryData.data;
            const overTimeObservations: ReportsSummaryOverTime[] =
              overTimeData != null ? overTimeData.data : [];

            // Then lets set up our data repositories
            const overallData: ReportsChartDatum[] = [];

            overTimeObservations.forEach((currentObservation) => {
              const currentDataPoints: ReportsOverTimeDatum[] = currentObservation.data;

              currentDataPoints.forEach((currentDataPoint) => {
                const timeIndex = overallData.findIndex((element) => {
                  return element.created_timestamp === currentDataPoint.created_timestamp;
                });

                // If theres an existing element with the timestamp, add to the element
                if (timeIndex !== -1) {
                  switch (currentObservation.run_type) {
                    case ReportRunTypes.First:
                      overallData[timeIndex] = {
                        ...overallData[timeIndex],
                        first_ready_count: currentDataPoint.ready_count,
                        first_match_rate: currentDataPoint.match_rate * 100,
                      };
                      break;
                    case ReportRunTypes.Subsequent:
                      overallData[timeIndex] = {
                        ...overallData[timeIndex],
                        subsequent_ready_count: currentDataPoint.ready_count,
                        subsequent_match_rate: currentDataPoint.match_rate * 100,
                      };
                      break;
                    default:
                      assertNever(currentObservation.run_type);
                  }
                } else {
                  // Otherwise append the current element
                  switch (currentObservation.run_type) {
                    case ReportRunTypes.First:
                      overallData.push({
                        created_timestamp: currentDataPoint.created_timestamp,
                        first_ready_count: currentDataPoint.ready_count,
                        first_match_rate: currentDataPoint.match_rate * 100,
                      });
                      break;
                    case ReportRunTypes.Subsequent:
                      overallData.push({
                        created_timestamp: currentDataPoint.created_timestamp,
                        subsequent_ready_count: currentDataPoint.ready_count,
                        subsequent_match_rate: currentDataPoint.match_rate * 100,
                      });
                      break;
                    default:
                      assertNever(currentObservation.run_type);
                  }
                }
              });
            });

            // Need to sort the data before we pass it into the fillChartData, otherwise it pukes
            overallData.sort((first: ReportsChartDatum, second: ReportsChartDatum) => {
              return first.created_timestamp - second.created_timestamp;
            });

            const effectivePeriod =
              granularities != null
                ? granularities[summaryData.token as ReportTemplateToken]
                : reportsOverTime.period;

            // Now lets fill the data
            const filledData: ReportsChartDatum[] = fillChartData(
              overallData,
              BASE_VALUE,

              granularities != null &&
                lastQueryFilters?.createdAtEnd != null &&
                lastQueryFilters?.createdAtStart != null &&
                // In the event that we don't have permissions to some templates, we'll have an empty granularity
                // We'll then need to ensure that we're looking at the default granularity
                granularities[summaryData.token as ReportTemplateToken] != null
                ? generateDomain(
                    [
                      // These are in MS, need to divide
                      Date.parse(lastQueryFilters.createdAtStart) / 1000,
                      Date.parse(lastQueryFilters.createdAtEnd) / 1000,
                    ],
                    granularities[summaryData.token as ReportTemplateToken],
                  )
                : reportsOverTime.domain,
              effectivePeriod,
              true,
              true,
            );

            // For data with 0 on the ready count, we'll need it to be 0 on the
            // match rate as well. Same is true for when it doesnt exist at all
            const interpretedData = filledData.map((datum) => {
              if (datum.first_ready_count === 0 || datum.first_ready_count == null) {
                delete datum.first_match_rate;
              }

              if (datum.subsequent_ready_count === 0 || datum.subsequent_ready_count == null) {
                delete datum.subsequent_match_rate;
              }

              return datum;
            });

            // In the event that we have 1 set of reports (subsequent or First), we'll have a different
            // set of line keys / translations
            const lineKeys: Record<string, string> = {};
            const barKeys: Record<string, string> = {};
            if (summaryData.data.length === 1) {
              const label =
                summaryData.data[0].run_type === ReportRunTypes.First ? '1st' : 'subsequent';
              barKeys[`${summaryData.data[0].run_type}${READY_SUFFIX}`] = isRecurrable
                ? `Ready (${label} runs)`
                : 'Ready';
              lineKeys[`${summaryData.data[0].run_type}${MATCH_RATE_SUFFIX}`] = isRecurrable
                ? `Match rate (${label} runs)`
                : 'Match rate';
            } else {
              barKeys.first_ready_count = 'Ready (1st runs)';
              barKeys.subsequent_ready_count = 'Ready (subsequent runs)';
              lineKeys.first_match_rate = 'Match rate (1st runs)';
              lineKeys.subsequent_match_rate = 'Match rate (subsequent runs)';
            }

            const firstRunsSummary =
              summaryObservations.find((datum) => datum.run_type === ReportRunTypes.First) ||
              EMPTY_FIRST_DATUM;

            const subsequentRunsSummary =
              summaryObservations.find((datum) => datum.run_type === ReportRunTypes.Subsequent) ||
              EMPTY_SUBSEQUENT_DATUM;

            return (
              <MetricsCard key={summaryData.id}>
                <ReportsMetricsSummaryContainer>
                  <HeaderContainer>
                    <StyledCardTitle data-test='report-metrics-card--name'>
                      {summaryData.name}
                    </StyledCardTitle>

                    <ReportsDateGranularitySelector
                      value={
                        granularities
                          ? granularities[summaryData.token as ReportTemplateToken]
                          : Period.Day
                      }
                      templateId={summaryData.token as ReportTemplateToken}
                      templateIds={templateIds}
                      handleUpdateGranularity={handleUpdateGranularity}
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      dateRange={[lastQueryFilters?.createdAtStart, lastQueryFilters?.createdAtEnd]}
                    />
                  </HeaderContainer>
                  <Spacer height={2} />
                  {formatSummaryDatum(firstRunsSummary, isRecurrable)}
                  {isRecurrable && formatSummaryDatum(subsequentRunsSummary, isRecurrable)}
                </ReportsMetricsSummaryContainer>
                <ReportsComposedCardBody>
                  <ReportsChartTitle>
                    Report metrics by {granularities[summaryData.token as ReportTemplateToken]}
                  </ReportsChartTitle>
                  <ReportsComposedChart
                    barKeys={barKeys}
                    lineKeys={lineKeys}
                    data={interpretedData}
                    xFormatter={
                      effectivePeriod === Period.Hour && reportsOverTime.period === effectivePeriod
                        ? formatChartDateHourChart
                        : effectivePeriod === Period.Month
                          ? formatChartMonthName
                          : formatChartDate
                    }
                    period={effectivePeriod}
                    legendIcons={isRecurrable ? LEGEND_ICONS : NON_RECURRING_ICONS}
                    interval={getEffectiveInterval(
                      reportsOverTime.period,
                      granularities[summaryData.token as ReportTemplateToken],
                    )}
                  />
                </ReportsComposedCardBody>
              </MetricsCard>
            );
          })}
    </ReportsMetricsContainer>
  );
};

export default ReportMetrics;
